import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { listStates, type StateRow } from '../../api/orgApi.js';
import { DataGrid } from '../../components/DataGrid.js';
import { ListShell, StatusBadge, sx } from './orgKit.js';

export function StateListPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<StateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listStates(token)
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <ListShell
      label="state-list"
      title={t('org.state.list.title')}
      newTo="/org/states/new"
      newLabel={t('org.state.list.new')}
      error={error}
      loading={loading}
      empty={!loading && rows.length === 0}
      emptyText={t('org.state.list.empty')}
    >
      {rows.length > 0 && (
        <DataGrid<StateRow>
          caption="states"
          rows={rows}
          getRowId={(s) => s.stateId}
          rowTestId={(s) => `state-${s.stateId}`}
          searchPlaceholder={t('org.common.searchNameCode')}
          columns={[
            {
              key: 'code',
              header: t('org.col.code'),
              value: (s) => s.stateCode,
              render: (s) => (
                <Link to={`/org/states/${s.stateId}/edit`} style={sx.link}>{s.stateCode}</Link>
              ),
            },
            { key: 'name', header: t('org.col.name'), value: (s) => s.stateName },
            { key: 'country', header: t('org.col.country'), value: (s) => s.countryCode },
            { key: 'gstCode', header: t('org.col.gstCode'), value: (s) => s.gstStateCode ?? '—' },
            {
              key: 'status',
              header: t('org.col.status'),
              value: (s) => s.recordStatusCode,
              render: (s) => <StatusBadge status={s.recordStatusCode} />,
            },
          ]}
        />
      )}
    </ListShell>
  );
}
